import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { memo, useCallback, useMemo, useRef, useState } from "react";
import { Accordion, AccordionDetails, AccordionSummary, Button, FormLabel, Grid, Typography, makeStyles } from "@material-ui/core";
import { AriaErrorScope, TextField, identityRecordOfFieldEditorOptions } from "@ploy-ui/form-fields";
import { isArray } from "lodash";
import { defineMessages, useIntl } from "react-intl";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import clsx from "clsx";
import { v4 as uuid } from "uuid";
import { Formik, Field as FormikField } from "formik";
import { addRegisteredSectionLayout } from "../../section/sectionLayoutDescriptions";
import { useAutoFocus } from "../../hooks/useAutoFocus";
import * as Yup from "yup";
import AddCircleOutlineIcon from "@material-ui/icons/AddCircleOutline";
import { CustomerRole, ErrorDisplay } from "@ploy-lib/types";
import { CalcRuleFormikField } from "../../field/CalcRuleFormikField";
import { createValidationHelpers } from "@ploy-lib/validation-helpers";
const useStyles = makeStyles((theme) => ({
    root: {},
    ownerDetails: {},
    listItem: {
        padding: theme.spacing(2)
    },
    expanded: {
        backgroundColor: theme.palette.primary.light,
        border: `1px solid ${theme.palette.secondary.light}`
    },
    childNumber: {
        paddingBottom: theme.spacing(2),
        paddingTop: theme.spacing(2)
    },
    header: {
        fontWeight: 700,
        paddingBottom: theme.spacing(4),
        padding: theme.spacing(0.5)
    },
    lenderLabel: {
        overflowWrap: "anywhere",
        fontWeight: "bold"
    },
    boldText: {
        fontWeight: "bold",
        fontSize: 16
    },
    addButton: {
        borderRadius: 4,
        justifyContent: "start",
        padding: "12px 24px",
        border: `1px solid ${theme.palette.common.black}`,
        fontSize: 16,
        background: "#fff",
        marginTop: theme.spacing(2)
    },
    addButtonPlusIcon: {
        marginRight: 8
    },
    expandMoreIcon: {
        color: theme.palette.primary.main
    },
    spacingAboveField: {
        paddingTop: 12
    }
}), { name: "DployPropertyOwnersField" });
const messages = defineMessages({
    houseOwnerTitle: {
        id: "template-form.field.houseOwner.houseOwnerTitle",
        defaultMessage: "House owner"
    },
    name: {
        id: "template-form.field.houseOwner.name",
        defaultMessage: "Name"
    },
    ssn: {
        id: "template-form.field.houseOwner.ssn",
        defaultMessage: "Social security number"
    },
    mustBeValidSsn: {
        id: "template-form.field.houseOwner.mustBeValidSsn",
        defaultMessage: "SSN must be valid"
    },
    email: {
        id: "template-form.field.houseOwner.email",
        defaultMessage: "E-mail address"
    },
    phone: {
        id: "template-form.field.houseOwner.phone",
        defaultMessage: "Phone number"
    },
    save: {
        id: "template-form.field.houseOwner.save",
        defaultMessage: "Save"
    },
    update: {
        id: "template-form.field.houseOwner.update",
        defaultMessage: "Add"
    },
    delete: {
        id: "template-form.field.houseOwner.delete",
        defaultMessage: "Delete"
    },
    addMore: {
        id: "template-form.field.houseOwner.addMore",
        defaultMessage: "Add more"
    },
    owningFraction: {
        id: "template-form.field.houseOwner.owningFraction",
        defaultMessage: "Ownership fraction"
    },
    mustBeANumber: {
        id: "template-form.field.houseOwner.mustBeANumber",
        defaultMessage: "Must be a number"
    },
    required: {
        id: "template-form.field.houseOwner.required",
        defaultMessage: "Required"
    },
    atLeastOne: {
        id: "template-form.field.houseOwner.atLeastOne",
        defaultMessage: "Must be at least 1"
    },
    ownerPosition: {
        id: "template-form.field.houseOwner.ownerPosition",
        defaultMessage: "Owner {current} of {total}"
    }
});
const fieldsToTouch = [
    "personalNumber",
    "email",
    "phone",
    "owningFractionNumerator"
];
const fieldsToTouchDictionary = Object.fromEntries(fieldsToTouch.map(key => [key, true]));
function makeHouseOwnerSchema(intl, validationHelpers) {
    return Yup.object({
        personalNumber: Yup.string()
            .required(intl.formatMessage(messages.required))
            .test("is-valid-ssn", intl.formatMessage(messages.mustBeValidSsn), validationHelpers.validSsn),
        email: Yup.string().required(intl.formatMessage(messages.required)),
        phone: Yup.string().required(intl.formatMessage(messages.required)),
        owningFractionNumerator: Yup.number()
            .typeError(intl.formatMessage(messages.mustBeANumber))
            .required(intl.formatMessage(messages.required))
            .min(1, intl.formatMessage(messages.atLeastOne))
    });
}
const CustomerType = {
    NotSet: 0,
    Private: 1,
    Business: 2,
    SelfEmployed: 3
};
const doApplicationCustomerSearch = (parameters) => fetch("/api/application-customers/search", {
    method: "POST",
    body: new Blob([JSON.stringify(parameters)], {
        type: "application/json"
    })
}).then(r => {
    if (!r.ok)
        return null;
    return r.json();
});
const doApplicationCustomerRemove = (parameters) => fetch(`/api/application-customers/clear-application-customer`, {
    method: "POST",
    body: new Blob([JSON.stringify(parameters)], {
        type: "application/json"
    })
}).then(r => {
    if (!r.ok) {
        return null;
    }
    return r.json();
});
const doApplicationCustomerUpdate = (parameters) => fetch("/api/application-customers/customer", {
    method: "PUT",
    body: new Blob([JSON.stringify(parameters)], {
        type: "application/json"
    })
}).then(res => res.ok);
const PropertyOwnerElement = (props) => {
    const classes = useStyles();
    const intl = useIntl();
    const validationHelpers = useMemo(() => createValidationHelpers(), []);
    const ownerSchema = useMemo(() => makeHouseOwnerSchema(intl, validationHelpers), [intl, validationHelpers]);
    const [expanded, setExpanded] = useState(!ownerSchema.isValidSync(props.data));
    const { ref: accordionRef } = useAutoFocus(!!props.autoFocus, classes.ownerDetails);
    const { _isNewEntry } = props.data;
    const isExpanded = _isNewEntry || expanded;
    const ownerName = props.data.name?.trim() || intl.formatMessage(messages.houseOwnerTitle);
    const summaryAriaLabel = props.total && props.total > 1 && props.position
        ? `${intl.formatMessage(messages.ownerPosition, {
            current: props.position,
            total: props.total
        })}, ${ownerName}`
        : ownerName;
    const baseId = `property-owner-${getOwnerId(props.data)}`;
    const [isRemoving, setIsRemoving] = useState(false);
    const handleOnRemove = useCallback(async () => {
        if (props.onDelete != null) {
            if (!props.data._isNewEntry && props.data.personalNumber?.trim() !== "") {
                setIsRemoving(true);
                await doApplicationCustomerRemove({
                    customerRole: CustomerRole.HouseOwner,
                    customerNumberHint: props.data.personalNumber
                });
                // No need to setIsRemoving(false)
            }
            props.onDelete(props.data);
        }
    }, [props]);
    return (_jsx(Formik, { initialValues: props.data, enableReinitialize: true, validationSchema: ownerSchema, onSubmit: async (ownerToUpdate, helpers) => {
            // Possibly look up new customer object as house owner
            if (ownerToUpdate.personalNumber != null &&
                ownerToUpdate.personalNumber.trim() !==
                    props.data.personalNumber?.trim()) {
                // Remove possibly unused house owner
                if (props.data.personalNumber?.trim() !== "") {
                    await doApplicationCustomerRemove({
                        customerNumberHint: props.data.personalNumber,
                        customerRole: CustomerRole.HouseOwner
                    });
                }
                // Look up customer
                const result = await doApplicationCustomerSearch({
                    customerType: CustomerType.Private,
                    customerRole: CustomerRole.HouseOwner,
                    customerNumber: ownerToUpdate.personalNumber,
                    noReplace: true
                });
                if (result == null) {
                    // Then what? Block update? Print no customer found?
                    helpers.setErrors({
                        personalNumber: "No customer found"
                    });
                    return Promise.reject();
                }
                if (result.foundCustomer.customerName) {
                    ownerToUpdate = {
                        ...ownerToUpdate,
                        name: result.foundCustomer.customerName
                    };
                }
            }
            // SHouldn't need to do the here? Can perform syncronization when DynCalcViewModelToDto runs?
            // Update the actual application customer object as well
            let appCustomerUpdate = null;
            if (ownerToUpdate.phone !== props.data.phone)
                (appCustomerUpdate ??= {}).phoneMobile = ownerToUpdate.phone;
            if (ownerToUpdate.email !== props.data.email)
                (appCustomerUpdate ??= {}).email = ownerToUpdate.email;
            if (appCustomerUpdate != null) {
                await doApplicationCustomerUpdate({
                    ...appCustomerUpdate,
                    customerNumber: ownerToUpdate.personalNumber
                });
            }
            props.onUpdate?.(ownerToUpdate);
            setExpanded(false);
            return Promise.resolve(ownerToUpdate);
        }, children: formikProps => (_jsxs(Accordion, { ref: accordionRef, expanded: isExpanded, onChange: async (_, updatedExpanded) => {
                const errorMessages = await formikProps.validateForm();
                // validateForm() doesn't set formikProps.isValid => false, or formikProps.isDirty => true..
                // Must instead take a look at validationresult to prevent closing of Accordion
                if (Object.keys(errorMessages).length > 0 || !formikProps.isValid) {
                    // Prevent closing if there are errors
                    formikProps.setTouched(fieldsToTouchDictionary, true);
                    return;
                }
                if (!formikProps.dirty)
                    setExpanded(updatedExpanded);
            }, className: clsx(classes.listItem, {
                [classes.expanded]: isExpanded
            }), children: [_jsx(AccordionSummary, { "aria-label": summaryAriaLabel, expandIcon: _jsx(ExpandMoreIcon, { className: classes.expandMoreIcon }), children: isExpanded ? (_jsx(Typography, { className: classes.lenderLabel, children: intl.formatMessage(messages.houseOwnerTitle) })) : (_jsx(Typography, { className: classes.boldText, children: props.data.name?.trim() ||
                            intl.formatMessage(messages.houseOwnerTitle) })) }), _jsx(AccordionDetails, { className: classes.ownerDetails, tabIndex: -1, "aria-label": summaryAriaLabel, children: _jsxs(Grid, { container: true, spacing: 4, direction: "column", children: [_jsx(Grid, { item: true, children: _jsx(FormikField, { name: "personalNumber", id: `${baseId}-personalNumber`, component: TextField, label: intl.formatMessage(messages.ssn), errorDisplay: ErrorDisplay.Touched, fullWidth: true }) }), _jsx(Grid, { item: true, children: _jsx(FormikField, { name: "email", id: `${baseId}-email`, component: TextField, label: intl.formatMessage(messages.email), errorDisplay: ErrorDisplay.Touched, fullWidth: true }) }), _jsx(Grid, { item: true, children: _jsx(FormikField, { name: "phone", id: `${baseId}-phone`, component: TextField, label: intl.formatMessage(messages.phone), errorDisplay: ErrorDisplay.Touched, fullWidth: true }) }), props.onUpdate != null ? (_jsxs(Grid, { item: true, container: true, justifyContent: "space-between", children: [_jsx(Button, { disabled: isRemoving ||
                                            formikProps.isSubmitting ||
                                            !formikProps.dirty ||
                                            !formikProps.isValid, onClick: () => {
                                            formikProps.submitForm();
                                        }, variant: "contained", children: _isNewEntry
                                            ? intl.formatMessage(messages.save)
                                            : intl.formatMessage(messages.update) }), props.onDelete != null ? (_jsx(Button, { onClick: handleOnRemove, variant: "contained", disabled: formikProps.isSubmitting || isRemoving, children: intl.formatMessage(messages.delete) })) : null] })) : null] }) })] })) }));
};
const PropertyOwnersInternal = memo((props) => {
    const intl = useIntl();
    const classes = useStyles();
    const listRef = useRef(null);
    const addButtonRef = useRef(null);
    const { owners } = props;
    const handleDelete = useCallback((owner) => {
        const index = owners.findIndex(o => getOwnerId(o) === getOwnerId(owner));
        props.onDelete?.(owner);
        requestAnimationFrame(() => {
            const items = listRef.current?.querySelectorAll(`.${classes.ownerDetails}`);
            const target = items?.[index] ?? items?.[index - 1];
            if (target) {
                target.focus();
            }
            else {
                addButtonRef.current?.focus();
            }
        });
    }, [owners, props, classes.ownerDetails]);
    return (_jsxs(Grid, { container: true, className: props.className, children: [_jsx(Grid, { container: true, spacing: 1, ref: listRef, children: owners.map((data, index) => (_jsx(Grid, { item: true, xs: 12, children: _jsx(PropertyOwnerElement, { data: data, autoFocus: props.autoFocusId != null &&
                            getOwnerId(data) === props.autoFocusId, multiplePropertyOwners: owners.length > 1, onUpdate: props.onUpdate, onDelete: props.onDelete ? handleDelete : undefined, position: index + 1, total: owners.length }) }, getOwnerId(data)))) }), props.onAdd != null ? (_jsx(Grid, { item: true, xs: 12, children: _jsxs(Button, { ref: addButtonRef, onClick: props.onAdd, variant: "outlined", className: classes.addButton, fullWidth: true, children: [_jsx(AddCircleOutlineIcon, { className: classes.addButtonPlusIcon }), intl.formatMessage(messages.addMore)] }) })) : null] }));
});
function getOwnerId(owner) {
    return owner._frontendOnlyId ?? String(owner.houseOwnerId);
}
function houseOwnerExistencePredicate(itemFromCollection, needle) {
    return ((needle._frontendOnlyId != null &&
        needle._frontendOnlyId === itemFromCollection._frontendOnlyId) ||
        (needle._frontendOnlyId == null &&
            needle.houseOwnerId === itemFromCollection.houseOwnerId));
}
function ensureCollectionHasIds(items) {
    return (!isArray(items) ? [] : items || []).map(owner => 
    // Just in case application / calc rules are reloaded but owner are not saved to database. May cause rerenders.
    (owner.houseOwnerId == null || owner.houseOwnerId === 0) &&
        !owner._frontendOnlyId
        ? { ...owner, _frontendOnlyId: uuid() }
        : owner);
}
function computeDisplayValue(owner) {
    const fraction = owner.owningFractionDenominator == null ||
        owner.owningFractionDenominator === 0
        ? 100
        : (100 * (owner.owningFractionNumerator ?? 0)) /
            owner.owningFractionDenominator;
    const displayValue = `${owner.name ?? ""};${Math.floor(fraction * 100) / 100};${getOwnerId(owner)}}`; // The last one just to ensure unique values
    return displayValue;
}
function calculateNewFractions(updatedList) {
    const updatedDenominator = updatedList.reduce((acc, o) => acc +
        (o.owningFractionNumerator == null || o.owningFractionNumerator === 0
            ? 1
            : o.owningFractionNumerator), 0);
    updatedList = updatedList.map(owner => {
        const updatedOwner = {
            ...owner,
            owningFractionDenominator: updatedDenominator
        };
        updatedOwner.displayValue = computeDisplayValue(updatedOwner);
        return updatedOwner;
    });
    return updatedList;
}
export const PropertyOwnersField = memo((props) => {
    const classes = useStyles();
    const owners = useMemo(() => ensureCollectionHasIds(props.field.value), [props.field.value]);
    const autoFocusIdRef = useRef();
    const addItem = useCallback(() => {
        const newId = uuid();
        let updatedList = [
            ...owners,
            {
                _frontendOnlyId: newId,
                _isNewEntry: true,
                owningFractionNumerator: 1
            }
        ];
        updatedList = calculateNewFractions(updatedList);
        autoFocusIdRef.current = newId;
        props.form.setFieldValue(props.field.name, updatedList);
    }, [owners, props.field.name, props.form]);
    const deleteItem = useCallback((ownerToDelete) => {
        let updatedList = owners.filter(owner => !houseOwnerExistencePredicate(owner, ownerToDelete));
        updatedList = calculateNewFractions(updatedList);
        props.form.setFieldValue(props.field.name, updatedList);
    }, [owners, props.field.name, props.form]);
    const updateItem = useCallback((updatedData) => {
        let updatedList = owners.map(owner => {
            let entry = houseOwnerExistencePredicate(owner, updatedData)
                ? updatedData
                : owner;
            delete entry._isNewEntry;
            return entry;
        });
        // TODO: do extra validation? Ensure unique ssns?
        updatedList = calculateNewFractions(updatedList);
        props.form.setFieldValue(props.field.name, updatedList);
        return true;
    }, [owners, props.field.name, props.form]);
    const maxOwners = 5;
    return (_jsxs(AriaErrorScope, { component: Grid, form: props.form, fieldName: props.field.name, errorDisplay: props.errorDisplay, container: true, spacing: 1, "aria-label": typeof props.label === "string"
            ? props.label
            : props.options?.buttonText, className: clsx(classes.root, props.className), onClick: props.onClick, children: [props.label ? (_jsx(Grid, { item: true, xs: 12, children: _jsx(FormLabel, { component: "legend", children: props.label }) })) : null, _jsx(Grid, { item: true, xs: 12, children: _jsx(PropertyOwnersInternal, { owners: owners, autoFocusId: autoFocusIdRef.current, onAdd: owners.length < maxOwners ? addItem : undefined, onUpdate: updateItem, onDelete: deleteItem }) })] }));
});
export const PropertyOwnersSection = (sectionProps) => {
    // This section started as a custom field 😅
    return (_jsx(_Fragment, { children: _jsx(CalcRuleFormikField, { component: PropertyOwnersField, name: "Calculator.HouseOwnersMainResidenceOrFirst", className: sectionProps.className, onClick: sectionProps.onClick, literal: sectionProps.literal }) }));
};
addRegisteredSectionLayout({
    displayName: "PropertyOwnersSection",
    name: "PropertyOwnersSection",
    settings: {}
});
PropertyOwnersField.displayName = "PropertyOwnersField";
PropertyOwnersSection.displayName = "PropertyOwnersSection";
export const EditorPropertyOwnerFields = identityRecordOfFieldEditorOptions({
    PropertyOwnersField: {
        editableOptions: {}
    }
});
//# sourceMappingURL=PropertyOwnersSection.js.map